import FirestoreClient from '../clients/FirestoreClient.js';
import AuthClient from '../clients/AuthClient.js';
import { escapeHTML } from '../utils/common.js';
import TimeService from '../utils/TimeService.js';
import errorHandler from '../handlers/ErrorHandler.js';
import aiVerificationService from '../services/AIVerificationService.js';
import { detectRegion } from '../utils/regionDetection.js';
import { withTimeout } from '../utils/withTimeout.js';
import { trackFormInteraction } from '../services/AnalyticsService.js';
import { FORM, FIRESTORE, ERROR } from '../constants/index.js';

class IdeaFormViewModel {
    #ideasCache = new Map();

    constructor(currentLangObservable) {
        this.currentLang = currentLangObservable;
    }

    escapeHTML = escapeHTML

    getIdeasCollectionName = () => {
        const lang = (this.currentLang() || 'en').toLowerCase();
        const region = detectRegion();
        if (lang === 'pl') return FIRESTORE.COLLECTIONS.IDEAS_PL;
        if (region === 'eu') return FIRESTORE.COLLECTIONS.IDEAS_EUEN;
        return FIRESTORE.COLLECTIONS.IDEAS_EN;
    }

    // --- Private helpers ---
    #getIdeasCacheKey() {
        const lang = (this.currentLang() || 'en').toLowerCase();
        const region = detectRegion();
        return `${lang}-${region}-${this.getIdeasCollectionName()}`;
    }

    #renderIdeasList(list, ideas) {
        list.innerHTML = '';
        if (!ideas.length) {
            list.innerHTML = '<li class="text-slate-500">' + window.Engryv.i18n.t('products.noProducts') + '</li>';
            return;
        }
        const fragment = document.createDocumentFragment();
        let idx = 0;
        ideas.forEach(idea => {
            const listItem = document.createElement('li');
            const bgClass = idx % 2 === 0 ? 'bg-gradient-to-r from-blue-50 via-white to-blue-50' : 'bg-gradient-to-r from-slate-100 via-white to-slate-100';
            listItem.className = `
                border-2 border-blue-100 
                rounded-xl 
                p-5 
                ${bgClass} 
                shadow 
                hover:shadow-lg 
                transition-shadow 
                duration-200
                relative
                group
            `.replace(/\s+/g, ' ').trim();
            const formattedDate = this.#formatIdeaDate(idea.createdAt);
            listItem.innerHTML = `
                <div class="flex items-center gap-2 mb-2">
                    <span class="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100">
                        <svg class="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 20a8 8 0 100-16 8 8 0 000 16z"/>
                        </svg>
                    </span>
                    <h4 class="font-bold text-blue-800 text-lg tracking-tight">${this.escapeHTML(idea.title)}</h4>
                </div>
                <p class="text-base text-slate-700 mb-3 pl-9 border-l-4 border-blue-100">${this.escapeHTML(idea.description)}</p>
                <div class="flex items-center justify-between mt-2">
                    <span class="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full shadow-sm">Submitted: ${formattedDate}</span>
                </div>
            `;
            fragment.appendChild(listItem);
            idx++;
        });
        list.appendChild(fragment);
    }
    #formatIdeaDate(createdAt) {
        return TimeService.formatDateForDisplay(createdAt);
    }

    #setIdeaFormMessage(ideaFormMessage, text, ...classes) {
        if (!ideaFormMessage) return;
        ideaFormMessage.textContent = window.Engryv.i18n.t(text) || text;
        ideaFormMessage.className = 'text-sm mt-2 ' + classes.join(' ');
    }

    #renderIdeasError(list, errorMessage) {
        const retryButton = document.createElement('button');
        retryButton.className = 'mt-3 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors duration-200 cursor-pointer';
        retryButton.innerHTML = window.Engryv.i18n.t('productDetail.retry') || 'Retry';
        retryButton.addEventListener('click', () => this.loadIdeas());
        
        list.innerHTML = '';
        const li = document.createElement('li');
        li.className = 'text-red-500';
        li.textContent = errorMessage;
        list.appendChild(li);
        list.appendChild(retryButton);
    }

    #setSubmitButtonLoading(submitButton, spinner, isLoading) {
        if (submitButton) submitButton.disabled = isLoading;
        if (spinner) spinner.style.display = isLoading ? 'inline-block' : 'none';
    }

    #aiVerifyIdea = async (title, description, ideaFormMessage) => {
        try {
            // Show verifying message
            this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.verifyingAI'), 'text-blue-600');

            // Call the AI verification service
            const verificationResult = await withTimeout(
                aiVerificationService.verify(
                    title,
                    description,
                    this.currentLang(),
                    window.location.hostname
                ),
                15000,
                'aiVerifyIdea: aiVerificationService.verify'
            );

            // Check if the idea should be rejected
            const rejection = aiVerificationService.checkRejectionReasons(verificationResult);
            
            if (rejection.rejected) {
                const cssClass = rejection.severity === 'warning' ? 'text-yellow-600' : 'text-red-600';
                this.#setIdeaFormMessage(ideaFormMessage, rejection.reason, cssClass);
                return false;
            }

            return true;

        } catch (err) {
            // Log the error for debugging
            errorHandler.handle(err, { operation: 'aiVerifyIdea' }, 'error');

            // Show specific input validation errors to the user
            if (err.message && err.message.startsWith('Input validation failed:')) {
                this.#setIdeaFormMessage(
                    ideaFormMessage,
                    err.message.replace('Input validation failed: ', ''),
                    'text-red-600'
                );
                return false;
            }

            // Specific handling for common Firebase errors
            let errorMessage = 'AI verification failed. Please try again later.';
            if (err.code === 'unauthenticated') errorMessage = 'Please log in to submit ideas.';
            if (err.code === 'permission-denied') errorMessage = 'App Check failed. Try again from a real device.';

            // Show user-facing error popup
            errorHandler.showErrorPopup(errorMessage, 'Submission Error');

            this.#setIdeaFormMessage(
                ideaFormMessage,
                errorMessage,
                'text-red-600'
            );
            return false;
        }
    }

    // --- Public Methods ---
    loadIdeas = async () => {
        const list = document.getElementById('recent-ideas-list');
        if (!list) return;
        
        // Check cache first
        const cacheKey = this.#getIdeasCacheKey();
        if (this.#ideasCache.has(cacheKey)) {
            const cachedIdeas = this.#ideasCache.get(cacheKey);
            this.#renderIdeasList(list, cachedIdeas);
            return;
        }
        
        list.innerHTML = '<li class="flex items-center gap-2 text-slate-600"><span class="' + FORM.SPINNER_CLASS + '"></span>' + window.Engryv.i18n.t('productDetail.loading') + '</li>';
        try {
            const ideas = await withTimeout(
                FirestoreClient.queryCollection(this.getIdeasCollectionName(), {
                    orderByField: FIRESTORE.QUERY.ORDER_BY_FIELD,
                    order: FIRESTORE.QUERY.ORDER_DIRECTION,
                    limitNum: FIRESTORE.QUERY.RECENT_IDEAS_LIMIT
                }),
                10000,
                'loadIdeas: queryCollection'
            );
            
            // Cache the ideas
            this.#ideasCache.set(cacheKey, ideas);
            
            // Render ideas
            this.#renderIdeasList(list, ideas);
        } catch (err) {
            errorHandler.handle(err, { operation: 'loadIdeas' }, 'error');
            const errorMessage = err.code === 'timeout' 
                ? 'Network timeout loading ideas. Please try again.'
                : window.Engryv.i18n.t('productDetail.notFound') || 'Failed to load ideas';
            this.#renderIdeasError(list, errorMessage);
        }
    }

    initializeIdeaForm = () => {
        const form = document.getElementById('idea-form');
        const ideaFormMessage = document.getElementById('idea-form-message');
        if (!form) return;
        
        // Remove any action attribute that would cause form reload
        if (form.hasAttribute('action')) {
            form.removeAttribute('action');
        }
        form.setAttribute('onsubmit', 'return false;');
        
        if (form.dataset.listenerAttached === 'true') {
            this.loadIdeas();
            return;
        }
        form.dataset.listenerAttached = 'true';

        const submitButton = form.querySelector('button[type="submit"]');

        // Create a spinner element (hidden by default)
        let spinner = submitButton?.querySelector('.idea-form-spinner');
        if (!spinner) {
            spinner = document.createElement('span');
            spinner.className = FORM.SPINNER_CLASS;
            spinner.style.display = 'none';
            if (submitButton) submitButton.appendChild(spinner);
        }

        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            try {
                const titleInput = document.getElementById('idea-title');
                const descriptionInput = document.getElementById('idea-description');
                const title = titleInput.value.trim();
                const description = descriptionInput.value.trim();

                const wordCount = description.split(/\s+/).filter(Boolean).length;
                if (wordCount > FORM.MAX_IDEA_DESCRIPTION_WORDS) {
                    this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.wordLimit'), FORM.MESSAGE_COLORS.ERROR);
                    return;
                }

                this.#setIdeaFormMessage(ideaFormMessage, '', 'text-sm', 'mt-2');
                if (!title || !description) {
                    this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.fillOutBoth'), 'text-red-600');
                    return;
                }

                // --- Disable submit button & show spinner ---
                this.#setSubmitButtonLoading(submitButton, spinner, true);
                this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.verifyingAI'), 'text-blue-600');

                // --- AI Verification ---
                const aiApproved = await this.#aiVerifyIdea(title, description, ideaFormMessage);

                // --- Re-enable submit button & hide spinner ---
                this.#setSubmitButtonLoading(submitButton, spinner, false);

                if (!aiApproved) return;

                const currentUser = AuthClient.getCurrentUser();
                const userId = currentUser ? currentUser.uid : 'anonymous';
                const ideaId = TimeService.generateDailyId(userId);

                // --- Show spinner again for checking existing idea ---
                this.#setSubmitButtonLoading(submitButton, spinner, true);
                this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.checkingExisting'), 'text-blue-600');
                const existingDoc = await withTimeout(
                    FirestoreClient.getDocument(this.getIdeasCollectionName(), ideaId),
                    10000,
                    'initializeIdeaForm: getDocument'
                );
                if (existingDoc) {
                    this.#setSubmitButtonLoading(submitButton, spinner, false);
                    this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.onePerDay'), 'text-yellow-600');
                    return;
                }

                this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.submitting'), 'text-blue-600');

                // Track form submission
                trackFormInteraction('idea_submission', { title_length: title.length, description_word_count: wordCount });

                const ideaData = {
                    title,
                    description,
                    createdAt: FirestoreClient.getServerTimestamp(),
                    userId,
                    status: 'submitted'
                };
                await withTimeout(
                    FirestoreClient.setDocument(this.getIdeasCollectionName(), ideaId, ideaData),
                    10000,
                    'initializeIdeaForm: setDocument'
                );

                this.#setSubmitButtonLoading(submitButton, spinner, false);
                this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.thankYou'), 'text-green-600');
                form.reset();
                this.#ideasCache.clear();  // Clear cache to fetch fresh ideas
                this.loadIdeas();

            } catch (err) {
                errorHandler.handle(err, { operation: 'ideaFormSubmit', stage: 'handleSubmit' }, 'error');
                this.#setIdeaFormMessage(ideaFormMessage, window.Engryv.i18n.t('ideaForm.submitError'), 'text-red-600');
                // Re-enable submit button if it's disabled
                this.#setSubmitButtonLoading(submitButton, spinner, false);
            }
        });

        this.loadIdeas();
    }
}

export default IdeaFormViewModel;
